import classNames from 'classnames';
import { type ComponentProps, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Close from '../../icons/Close';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import IconButton from '../IconButton';
import Popover from '../Popover';
import PlusLogoBadge from '../SubscriptionBadge/Logo/Plus';
import styles from './styles/styles.module.scss';

export type ButtonDefinition = Pick<ComponentProps<typeof Button>, 'disabled' | 'onClick' | 'testId' | 'text' | 'variant'>;

type PopupMenuProps = Pick<ComponentProps<typeof Popover>, 'anchor' | 'placement'> & {
  buttons?: ButtonDefinition[];
  children: React.ReactNode;
  containerClassName?: string;
  headerAndContentClassName?: string;
  hideCloseButton?: boolean;
  isOpen?: boolean;
  onCloseRequest: () => void;
  purchaseTier?: PurchaseTier;
  title?: ReactNode;
  useBottomSheet: boolean;
} & Pick<ComponentProps<typeof Popover>, 'placement'>;

export default function PopupMenu({
  anchor,
  buttons,
  children,
  containerClassName,
  headerAndContentClassName,
  hideCloseButton = false,
  isOpen = false,
  onCloseRequest,
  placement,
  purchaseTier,
  title,
  useBottomSheet
}: PopupMenuProps) {
  const intl = useIntl();

  const renderedButtons = useMemo(
    () =>
      buttons?.map(button => (
        <Button key={`popup-menu-${button.variant}-button`} {...(button as ButtonDefinition)} size={useBottomSheet ? 'md' : 'sm'} width="full" />
      )),
    [buttons, useBottomSheet]
  );

  const contents = useMemo(() => {
    const renderHeader = title || !hideCloseButton;
    return (
      <>
        <div className={classNames(styles.headerAndContent, headerAndContentClassName)}>
          {purchaseTier === 'plus' && <PlusLogoBadge className={styles.badge} variant="full" size="sm" />}
          {renderHeader && (
            <div className={styles.header}>
              {title && (
                <Typography className={styles.title} component="div" variant={useBottomSheet ? 'heading400' : 'heading300'}>
                  {title}
                </Typography>
              )}
              {!hideCloseButton && (
                <IconButton
                  className={styles.closeButton}
                  icon={{ Component: Close }}
                  onClick={onCloseRequest}
                  size="sm"
                  title={intl.formatMessage({ defaultMessage: 'Close' })}
                  variant="default"
                />
              )}
            </div>
          )}
          <div className={styles.content}>{children}</div>
        </div>
        {renderedButtons?.length ? <div className={styles.buttons}>{renderedButtons}</div> : null}
      </>
    );
  }, [title, hideCloseButton, headerAndContentClassName, purchaseTier, useBottomSheet, onCloseRequest, intl, children, renderedButtons]);

  return useBottomSheet ? (
    <>
      {anchor}
      <BottomSheet className={classNames(styles.bottomSheet, containerClassName)} isOpen={isOpen} onCloseRequest={onCloseRequest}>
        {contents}
      </BottomSheet>
    </>
  ) : (
    <Popover
      anchor={anchor}
      className={classNames(styles.popover, containerClassName)}
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      placement={placement}
    >
      {contents}
    </Popover>
  );
}
